import {
    type BarOHLCVDetailed,
    type VolumePriceAnalysis,
    type VolumePriceAnalysisEvent,
    VolumePriceAnalysisEventType
} from '@einhaus/project314-types';

export type AuditEventSensitivity = 'conservative' | 'standard' | 'aggressive';

export type VpaExplanationPredicate = {
    actualValue: string;
    expectedValue: string;
    label: string;
    note?: string;
    passed: boolean;
};

export type VpaExplanationMetric = {
    label: string;
    value: string;
};

export type VpaEventExplanation = {
    predicates: VpaExplanationPredicate[];
    rawBarMetrics: VpaExplanationMetric[];
    summary: string;
    title: string;
};

type Thresholds = {
    climacticVolumeThreshold: number;
    highVolumeThreshold: number;
    lowVolumeThreshold: number;
    narrowSpreadThreshold: number;
    trendLookbackBars: number;
    wideSpreadThreshold: number;
};

const formatMetric = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return 'n/a';
    }

    return value.toFixed(fractionDigits);
};

const formatPrice = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return 'n/a';
    }

    return value.toFixed(2);
};

const formatEventTypeLabel = (value: string): string =>
    value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (firstLetter) => firstLetter.toUpperCase());

const getThresholds = (eventSensitivity: AuditEventSensitivity): Thresholds => {
    if (eventSensitivity === 'conservative') {
        return {
            climacticVolumeThreshold: 3,
            highVolumeThreshold: 2,
            lowVolumeThreshold: 0.6,
            narrowSpreadThreshold: 0.7,
            trendLookbackBars: 6,
            wideSpreadThreshold: 1.75
        };
    }

    if (eventSensitivity === 'aggressive') {
        return {
            climacticVolumeThreshold: 2,
            highVolumeThreshold: 1.5,
            lowVolumeThreshold: 0.8,
            narrowSpreadThreshold: 0.9,
            trendLookbackBars: 4,
            wideSpreadThreshold: 1.25
        };
    }

    return {
        climacticVolumeThreshold: 2.4,
        highVolumeThreshold: 1.75,
        lowVolumeThreshold: 0.7,
        narrowSpreadThreshold: 0.8,
        trendLookbackBars: 5,
        wideSpreadThreshold: 1.45
    };
};

const buildPredicate = (params: {
    actualValue: string;
    expectedValue: string;
    label: string;
    note?: string;
    passed: boolean;
}): VpaExplanationPredicate => params;

const buildRawBarMetrics = (params: {
    bar: BarOHLCVDetailed;
    event: VolumePriceAnalysisEvent;
    state: VolumePriceAnalysis;
}): VpaExplanationMetric[] => {
    const { bar, event, state } = params;

    return [
        { label: 'Open', value: formatPrice(bar.o) },
        { label: 'High', value: formatPrice(bar.h) },
        { label: 'Low', value: formatPrice(bar.l) },
        { label: 'Close', value: formatPrice(bar.c) },
        { label: 'Volume', value: bar.v.toLocaleString() },
        { label: 'Trade Count', value: bar.tc?.toLocaleString() ?? 'n/a' },
        { label: 'Bar VWAP', value: formatPrice(bar.bv) },
        { label: 'Notional', value: bar.nv ? bar.nv.toLocaleString() : 'n/a' },
        { label: 'Confirmed Signal', value: event.referenceEventType ? formatEventTypeLabel(event.referenceEventType) : 'n/a' },
        { label: 'RVOL', value: formatMetric(state.participation.relativeVolume) },
        { label: 'RVOL TOD', value: formatMetric(event.relativeVolumeTod) },
        { label: 'Spread vs Typical', value: formatMetric(state.barStructure.spreadVsTypical) },
        { label: 'Body % Range', value: formatMetric(state.barStructure.bodyPctOfRange) },
        { label: 'CLV', value: formatMetric(state.barStructure.closeLocationValue) },
        { label: 'Upper Wick %', value: formatMetric(state.barStructure.upperWickPct) },
        { label: 'Lower Wick %', value: formatMetric(state.barStructure.lowerWickPct) }
    ];
};

const buildTrendPrerequisite = (eventType: VolumePriceAnalysisEventType, thresholds: Thresholds): VpaExplanationPredicate | null => {
    const bearishTrendTypes: VolumePriceAnalysisEventType[] = [
        VolumePriceAnalysisEventType.shakeout,
        VolumePriceAnalysisEventType.stoppingVolumeBottom,
        VolumePriceAnalysisEventType.climacticBottom
    ];

    const bullishTrendTypes: VolumePriceAnalysisEventType[] = [
        VolumePriceAnalysisEventType.upthrust,
        VolumePriceAnalysisEventType.stoppingVolumeTop,
        VolumePriceAnalysisEventType.climacticTop
    ];

    if (bullishTrendTypes.includes(eventType)) {
        return buildPredicate({
            actualValue: `implicit from emitted ${formatEventTypeLabel(eventType)} event`,
            expectedValue: `bullish recent trend bias (${thresholds.trendLookbackBars}-bar lookback)`,
            label: 'Trend prerequisite',
            note: 'The Rust analyzer requires a bullish recent-trend context for this event path but does not currently return the raw recentTrendBias value.',
            passed: true
        });
    }

    if (bearishTrendTypes.includes(eventType)) {
        return buildPredicate({
            actualValue: `implicit from emitted ${formatEventTypeLabel(eventType)} event`,
            expectedValue: `bearish recent trend bias (${thresholds.trendLookbackBars}-bar lookback)`,
            label: 'Trend prerequisite',
            note: 'The Rust analyzer requires a bearish recent-trend context for this event path but does not currently return the raw recentTrendBias value.',
            passed: true
        });
    }

    return null;
};

const isUpBar = (bar: BarOHLCVDetailed): boolean => bar.c > bar.o;
const isDownBar = (bar: BarOHLCVDetailed): boolean => bar.c < bar.o;

const barDirectionLabel = (bar: BarOHLCVDetailed): string => {
    if (isUpBar(bar)) return 'up bar';
    if (isDownBar(bar)) return 'down bar';
    return 'flat bar';
};

export const buildVpaEventExplanation = (params: {
    bar: BarOHLCVDetailed;
    event: VolumePriceAnalysisEvent;
    eventSensitivity: AuditEventSensitivity;
    state: VolumePriceAnalysis;
}): VpaEventExplanation => {
    const { bar, event, eventSensitivity, state } = params;
    const thresholds = getThresholds(eventSensitivity);
    const { relativeVolume } = state.participation;
    const { spreadVsTypical } = state.barStructure;
    const { closeLocationValue } = state.barStructure;
    const { upperWickPct } = state.barStructure;
    const { lowerWickPct } = state.barStructure;
    const breakoutQuality = state.context.breakoutFromCongestionQuality;
    const predicates: VpaExplanationPredicate[] = [];
    const trendPrerequisite = buildTrendPrerequisite(event.type, thresholds);

    if (trendPrerequisite) {
        predicates.push(trendPrerequisite);
    }

    const pushRelativeVolumePredicate = (label: string, comparator: '<=' | '>=', threshold: number) => {
        const passed =
            relativeVolume !== null &&
            relativeVolume !== undefined &&
            (comparator === '<=' ? relativeVolume <= threshold : relativeVolume >= threshold);

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(relativeVolume),
                expectedValue: `${comparator} ${threshold.toFixed(2)}`,
                label,
                passed
            })
        );
    };

    const pushSpreadPredicate = (label: string, comparator: '<=' | '>=', threshold: number) => {
        const passed =
            spreadVsTypical !== null &&
            spreadVsTypical !== undefined &&
            (comparator === '<=' ? spreadVsTypical <= threshold : spreadVsTypical >= threshold);

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(spreadVsTypical),
                expectedValue: `${comparator} ${threshold.toFixed(2)}`,
                label,
                passed
            })
        );
    };

    const pushClvPredicate = (label: string, comparator: '<=' | '>=', threshold: number) => {
        const passed =
            closeLocationValue !== null &&
            closeLocationValue !== undefined &&
            (comparator === '<=' ? closeLocationValue <= threshold : closeLocationValue >= threshold);

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(closeLocationValue),
                expectedValue: `${comparator} ${threshold.toFixed(2)}`,
                label,
                passed
            })
        );
    };

    const pushUpperWickPredicate = (threshold: number) => {
        const passed = upperWickPct !== null && upperWickPct !== undefined && upperWickPct >= threshold;

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(upperWickPct),
                expectedValue: `>= ${threshold.toFixed(2)}`,
                label: 'Upper wick rejection',
                passed
            })
        );
    };

    const pushLowerWickPredicate = (threshold: number) => {
        const passed = lowerWickPct !== null && lowerWickPct !== undefined && lowerWickPct >= threshold;

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(lowerWickPct),
                expectedValue: `>= ${threshold.toFixed(2)}`,
                label: 'Lower wick rejection',
                passed
            })
        );
    };

    const pushDirectionPredicate = (expectedDirection: 'up bar' | 'down bar') => {
        const actualDirection = barDirectionLabel(bar);
        const passed = expectedDirection === 'up bar' ? isUpBar(bar) : expectedDirection === 'down bar' ? isDownBar(bar) : false;

        predicates.push(
            buildPredicate({
                actualValue: actualDirection,
                expectedValue: expectedDirection,
                label: 'Bar direction',
                passed
            })
        );
    };

    if (event.type === VolumePriceAnalysisEventType.noDemand) {
        pushDirectionPredicate('up bar');
        pushRelativeVolumePredicate('Relative volume', '<=', thresholds.lowVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '<=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '<=', 0.2);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'No Demand requires an up bar on low relative volume, narrow spread, and a weak close.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.noSupply) {
        pushDirectionPredicate('down bar');
        pushRelativeVolumePredicate('Relative volume', '<=', thresholds.lowVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '<=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '>=', -0.2);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'No Supply requires a down bar on low relative volume, narrow spread, and a close that avoids finishing weak.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.supplyTest) {
        pushDirectionPredicate('down bar');
        pushRelativeVolumePredicate('Relative volume', '<=', thresholds.lowVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '<=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '>=', 0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'Supply Test requires a low-volume narrow down bar that closes near the high of its range.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.demandTest) {
        pushDirectionPredicate('up bar');
        pushRelativeVolumePredicate('Relative volume', '<=', thresholds.lowVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '<=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '<=', -0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'Demand Test requires a low-volume narrow up bar that closes near the low of its range.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.upthrust) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.highVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushUpperWickPredicate(0.35);
        pushClvPredicate('Close location value', '<=', -0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Upthrust requires prior bullish trend context plus a wide, high-volume rejection bar with a strong upper wick and weak close.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.stoppingVolumeTop) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.highVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushUpperWickPredicate(0.35);
        pushClvPredicate('Close location value', '<=', -0.2);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Stopping Volume Top requires prior bullish trend context plus a wide, high-volume rejection bar that closes meaningfully off the highs.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.climacticTop) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.climacticVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushClvPredicate('Close location value', '<=', -0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Climactic Top requires prior bullish trend context plus climactic volume, wide spread, and a close near the low of the bar.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.shakeout) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.highVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushLowerWickPredicate(0.35);
        pushClvPredicate('Close location value', '>=', 0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Shakeout requires prior bearish trend context plus a wide, high-volume rejection bar with a strong lower wick and strong close.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.stoppingVolumeBottom) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.highVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushLowerWickPredicate(0.35);
        pushClvPredicate('Close location value', '>=', 0.2);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Stopping Volume Bottom requires prior bearish trend context plus a wide, high-volume rejection bar that closes meaningfully off the lows.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.climacticBottom) {
        pushRelativeVolumePredicate('Relative volume', '>=', thresholds.climacticVolumeThreshold);
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.wideSpreadThreshold);
        pushClvPredicate('Close location value', '>=', 0.45);

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Climactic Bottom requires prior bearish trend context plus climactic volume, wide spread, and a close near the high of the bar.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.confirmationOfStrength) {
        predicates.push(
            buildPredicate({
                actualValue: event.referenceEventType ? formatEventTypeLabel(event.referenceEventType) : 'n/a',
                expectedValue: 'prior bullish VPA signal in the recent confirmation window',
                label: 'Confirmed signal',
                note: 'The Rust analyzer only emits Confirmation Of Strength when the current bar follows through after a recent bullish VPA seed event.',
                passed: event.referenceEventType !== null
            })
        );

        pushDirectionPredicate('up bar');
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '>=', 0.25);

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(state.barStructure.bodyPctOfRange),
                expectedValue: '>= 0.35',
                label: 'Body % of range',
                passed:
                    state.barStructure.bodyPctOfRange !== null &&
                    state.barStructure.bodyPctOfRange !== undefined &&
                    state.barStructure.bodyPctOfRange >= 0.35
            })
        );

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Confirmation Of Strength requires a recent bullish VPA seed plus an up bar with supportive spread, meaningful body, and a constructive close.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.confirmationOfWeakness) {
        predicates.push(
            buildPredicate({
                actualValue: event.referenceEventType ? formatEventTypeLabel(event.referenceEventType) : 'n/a',
                expectedValue: 'prior bearish VPA signal in the recent confirmation window',
                label: 'Confirmed signal',
                note: 'The Rust analyzer only emits Confirmation Of Weakness when the current bar follows through after a recent bearish VPA seed event.',
                passed: event.referenceEventType !== null
            })
        );

        pushDirectionPredicate('down bar');
        pushSpreadPredicate('Spread vs typical', '>=', thresholds.narrowSpreadThreshold);
        pushClvPredicate('Close location value', '<=', -0.25);

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(state.barStructure.bodyPctOfRange),
                expectedValue: '>= 0.35',
                label: 'Body % of range',
                passed:
                    state.barStructure.bodyPctOfRange !== null &&
                    state.barStructure.bodyPctOfRange !== undefined &&
                    state.barStructure.bodyPctOfRange >= 0.35
            })
        );

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary:
                'Confirmation Of Weakness requires a recent bearish VPA seed plus a down bar with supportive spread, meaningful body, and a weak close.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.breakoutConfirmed) {
        predicates.push(
            buildPredicate({
                actualValue: state.context.isInCongestion ? `yes (${state.context.congestionBars} bars)` : 'no',
                expectedValue: 'yes, prior congestion context present',
                label: 'Congestion context',
                passed: state.context.isInCongestion
            })
        );

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(breakoutQuality),
                expectedValue: '>= 0.45',
                label: 'Breakout quality',
                passed: breakoutQuality !== null && breakoutQuality !== undefined && breakoutQuality >= 0.45
            })
        );

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'Breakout Confirmed requires prior congestion and a breakout quality score of at least 0.45.',
            title: 'Why This Fired'
        };
    }

    if (event.type === VolumePriceAnalysisEventType.breakoutRejected) {
        predicates.push(
            buildPredicate({
                actualValue: state.context.isInCongestion ? `yes (${state.context.congestionBars} bars)` : 'no',
                expectedValue: 'yes, prior congestion context present',
                label: 'Congestion context',
                passed: state.context.isInCongestion
            })
        );

        predicates.push(
            buildPredicate({
                actualValue: formatMetric(breakoutQuality),
                expectedValue: '<= -0.45',
                label: 'Breakout quality',
                passed: breakoutQuality !== null && breakoutQuality !== undefined && breakoutQuality <= -0.45
            })
        );

        return {
            predicates,
            rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
            summary: 'Breakout Rejected requires prior congestion and a negative breakout quality score of at most -0.45.',
            title: 'Why This Fired'
        };
    }

    return {
        predicates,
        rawBarMetrics: buildRawBarMetrics({ bar, event, state }),
        summary: `${formatEventTypeLabel(event.type)} fired from the Rust analyzer path, but no dedicated explanation template has been added yet.`,
        title: 'Why This Fired'
    };
};
